/**
 * File Attachment Rules (FAR) — design tokens for light and dark themes.
 *
 * The app theme is "standard" (dark navy) or a customizable light theme
 * (see App.js getUserTheme → theme.customize). Base text/border colors come
 * from theme.customize so admin theme settings still apply; semantic colors
 * (success / warning / danger / info) are fixed per mode and meet WCAG AA
 * contrast on their own backgrounds. State is never shown by color alone —
 * every tone is paired with an icon and text in the components.
 */
import { whiteColor } from "../../material-kit-pro-react.jsx";

export const isDarkTheme = (theme) => {
    try{
        return theme.customize.whiteBlackColor === whiteColor;
    }catch(e){ }
    return false;
};

export const farTokens = (theme) => {
    const dark = isDarkTheme(theme);
    const c = theme.customize || {};
    if(dark){
        return {
            dark: true,
            text: c.color || "#FFFFFF",
            textStrong: "#FFFFFF",
            textMuted: "#A9B1C7",
            surface: "#141A2C",
            surfaceAlt: "#1A2238",
            surfaceHover: "#1F2944",
            inputBg: "#0F1424",
            border: "#2A3352",
            borderStrong: "#3A4466",
            link: "#6EA8FF",
            accent: "#3B82F6",
            accentText: "#FFFFFF",
            primary: "#FAAF42",
            primaryHover: "#FFBE5C",
            primaryText: "#1A1206",
            disabledBg: "#2A3047",
            disabledText: "#7D859C",
            focus: "#6EA8FF",
            shadow: "0 8px 24px rgba(0,0,0,0.35)",
            success: { fg: "#4ADE80", bg: "rgba(34,197,94,0.12)", border: "rgba(74,222,128,0.45)", solid: "#22C55E", onSolid: "#06210F" },
            danger: { fg: "#FCA5A5", bg: "rgba(239,68,68,0.12)", border: "rgba(248,113,113,0.5)", solid: "#EF4444", onSolid: "#FFFFFF" },
            warning: { fg: "#FCD34D", bg: "rgba(245,158,11,0.12)", border: "rgba(252,211,77,0.45)", solid: "#F59E0B", onSolid: "#1A1206" },
            info: { fg: "#93C5FD", bg: "rgba(59,130,246,0.12)", border: "rgba(147,197,253,0.45)", solid: "#3B82F6", onSolid: "#FFFFFF" },
            neutral: { fg: "#CBD2E1", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.35)", solid: "#64748B", onSolid: "#FFFFFF" }
        };
    }
    return {
        dark: false,
        text: c.color || "#1C2033",
        textStrong: "#11142A",
        textMuted: "#5B6275",
        surface: "#FFFFFF",
        surfaceAlt: "#F6F7FB",
        surfaceHover: "#EEF1F8",
        inputBg: "#FFFFFF",
        border: "#E1E5EE",
        borderStrong: "#C9CFDC",
        link: c.linksColor || "#2563EB",
        accent: "#2563EB",
        accentText: "#FFFFFF",
        primary: "#FAAF42",
        primaryHover: "#F59E0B",
        primaryText: "#1A1206",
        disabledBg: "#E6E8EF",
        disabledText: "#7A8194",
        focus: "#2563EB",
        shadow: "0 6px 18px rgba(17,20,42,0.08)",
        success: { fg: "#166534", bg: "#ECFDF3", border: "#86EFAC", solid: "#16A34A", onSolid: "#FFFFFF" },
        danger: { fg: "#B91C1C", bg: "#FEF2F2", border: "#FCA5A5", solid: "#DC2626", onSolid: "#FFFFFF" },
        warning: { fg: "#92400E", bg: "#FFFBEB", border: "#FCD34D", solid: "#D97706", onSolid: "#FFFFFF" },
        info: { fg: "#1D4ED8", bg: "#EFF6FF", border: "#93C5FD", solid: "#2563EB", onSolid: "#FFFFFF" },
        neutral: { fg: "#475569", bg: "#F1F5F9", border: "#CBD5E1", solid: "#64748B", onSolid: "#FFFFFF" }
    };
};

/** Shared building blocks used by the FAR screens. */
export const farShared = (theme) => {
    const t = farTokens(theme);
    const focusRing = {
        "&:focus-visible": { outline: "2px solid "+t.focus, outlineOffset: "2px" }
    };
    return {
        t,
        focusRing,
        card: {
            backgroundColor: t.surface,
            border: "1px solid "+t.border,
            borderRadius: "12px",
            boxShadow: t.dark ? "none" : t.shadow,
            color: t.text
        },
        primaryButton: {
            ...focusRing,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            minHeight: "44px",
            padding: "0 24px",
            border: "none",
            borderRadius: "8px",
            backgroundColor: t.primary,
            color: t.primaryText,
            fontSize: "16px",
            fontWeight: 700,
            cursor: "pointer",
            textTransform: "none",
            "&:hover": { backgroundColor: t.primaryHover },
            "&:disabled, &[disabled]": { backgroundColor: t.disabledBg, color: t.disabledText, cursor: "not-allowed" }
        },
        secondaryButton: {
            ...focusRing,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            minHeight: "40px",
            padding: "0 16px",
            borderRadius: "8px",
            border: "1px solid "+t.accent,
            backgroundColor: "transparent",
            color: t.dark ? t.link : t.accent,
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            textTransform: "none",
            "&:hover": { backgroundColor: t.info.bg },
            "&:disabled, &[disabled]": { borderColor: t.border, color: t.disabledText, cursor: "not-allowed", backgroundColor: "transparent" }
        },
        accentButton: {
            ...focusRing,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            minHeight: "40px",
            padding: "0 18px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: t.accent,
            color: t.accentText,
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { filter: "brightness(1.08)" },
            "&:disabled, &[disabled]": { backgroundColor: t.disabledBg, color: t.disabledText, cursor: "not-allowed" }
        },
        ghostIconButton: {
            ...focusRing,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "34px",
            height: "34px",
            borderRadius: "8px",
            border: "none",
            background: "transparent",
            color: t.text,
            cursor: "pointer",
            "&:hover": { backgroundColor: t.surfaceHover },
            "& svg": { width: "20px", height: "20px" }
        },
        input: {
            ...focusRing,
            width: "100%",
            minHeight: "42px",
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid "+t.borderStrong,
            backgroundColor: t.inputBg,
            color: t.text,
            fontSize: "14px",
            fontFamily: "inherit",
            boxSizing: "border-box",
            "&::placeholder": { color: t.textMuted, opacity: 1 },
            "&:focus": { borderColor: t.focus, outline: "none", boxShadow: "0 0 0 2px "+t.info.bg }
        },
        label: {
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            color: t.text,
            marginBottom: "6px"
        },
        muted: { color: t.textMuted },
        badge: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "32px",
            height: "24px",
            padding: "0 8px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: t.info.bg,
            color: t.dark ? t.textStrong : t.info.fg,
            border: "1px solid "+t.info.border
        }
    };
};

export default farTokens;
